import { useState, useEffect } from "react";
import {
  Download,
  XCircle,
  Eye,
  MoreHorizontal,
  Search,
  Filter,
  X,
  AlertCircle,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  DataToolBar,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@b2b/ui-components";
import { rfqService, adminService } from "@b2b/services";
import { Rfq, RfqQuote } from "@b2b/services/api/modules/rfq/rfq.types";
import { AdminDisputeManagement } from "@b2b/ui-components/features/Admin/components/AdminDisputeManagement";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@b2b/ui-components";

interface ToolbarState {
  q: string;
  status: string;
  country: string;
  buyerId: string;
  sellerId: string;
  categoryId: string;
  from?: Date;
  to?: Date;
  pageSize: number;
}

interface ToastState {
  type: "success" | "error";
  message: string;
}

export default function AdminRFQManagement() {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState<Rfq[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [forceCloseDialog, setForceCloseDialog] = useState<{
    open: boolean;
    rfqId: string | null;
  }>({
    open: false,
    rfqId: null,
  });
  const [suspendDialog, setSuspendDialog] = useState<{
    open: boolean;
    sellerId: string | null;
    suspend: boolean;
  }>({
    open: false,
    sellerId: null,
    suspend: true,
  });
  const [quotesDialogOpen, setQuotesDialogOpen] = useState(false);
  const [selectedRfqForQuotes, setSelectedRfqForQuotes] = useState<
    string | null
  >(null);
  const [quotes, setQuotes] = useState<RfqQuote[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [categories, setCategories] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [buyers, setBuyers] = useState<
    Array<{ id: string; email: string; company?: { name: string } }>
  >([]);
  const [sellers, setSellers] = useState<
    Array<{ id: string; email: string; company?: { name: string } }>
  >([]);

  const [toolbar, setToolbar] = useState<ToolbarState>({
    q: "",
    status: "all",
    country: "all",
    buyerId: "all",
    sellerId: "all",
    categoryId: "all",
    pageSize: 20,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const setToolbarPartial = (updates: Partial<ToolbarState>) => {
    setToolbar((prev) => ({ ...prev, ...updates }));
  };

  // Fetch categories, buyers, sellers for filters
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [categoriesRes, usersRes] = await Promise.all([
          adminService.getCategories(),
          adminService.getAllUsers({ page: 1, limit: 1000 }),
        ]);
        setCategories(categoriesRes || []);
        if (usersRes.data) {
          const users = usersRes.data;
          setBuyers(
            users.filter(
              (u: any) => u.role === "BUYER" || u.role === "BUYER_SELLER"
            )
          );
          setSellers(
            users.filter(
              (u: any) => u.role === "SELLER" || u.role === "BUYER_SELLER"
            )
          );
        }
      } catch (error) {
        console.error("Error fetching filter data:", error);
      }
    };
    fetchFilterData();
  }, []);

  useEffect(() => {
    fetchRfqs();
  }, [currentPage, toolbar]);

  const fetchRfqs = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit: toolbar.pageSize,
        search: toolbar.q || undefined,
        status: toolbar.status !== "all" ? toolbar.status : undefined,
        buyerId: toolbar.buyerId !== "all" ? toolbar.buyerId : undefined,
        sellerId: toolbar.sellerId !== "all" ? toolbar.sellerId : undefined,
        categoryId:
          toolbar.categoryId !== "all" ? toolbar.categoryId : undefined,
        startDate: toolbar.from ? toolbar.from.toISOString() : undefined,
        endDate: toolbar.to ? toolbar.to.toISOString() : undefined,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      const response = await rfqService.getAdminRfqList(params);

      if (response && response.items) {
        setRfqs(response.items);
        setTotalPages(response.totalPages || 1);
        setTotal(response.total || 0);
      }
    } catch (error: any) {
      console.error("Error fetching RFQs:", error);
      showToast(
        "error",
        error?.response?.data?.message || "Failed to load RFQs"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForceClose = async () => {
    if (!forceCloseDialog.rfqId) return;
    try {
      setActionLoading(forceCloseDialog.rfqId);
      await rfqService.forceCloseRfq(forceCloseDialog.rfqId);
      showToast("success", "RFQ force closed successfully");
      setForceCloseDialog({ open: false, rfqId: null });
      fetchRfqs();
    } catch (error: any) {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to force close RFQ"
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspendSeller = async () => {
    if (!suspendDialog.sellerId) return;
    try {
      setActionLoading(suspendDialog.sellerId);
      await rfqService.suspendSellerFromQuoting(
        suspendDialog.sellerId,
        suspendDialog.suspend
      );
      showToast(
        "success",
        `Seller ${suspendDialog.suspend ? "suspended" : "unsuspended"} successfully`
      );
      setSuspendDialog({ open: false, sellerId: null, suspend: true });
      fetchRfqs();
    } catch (error: any) {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to update seller status"
      );
    } finally {
      setActionLoading(null);
    }
  };

  const fetchQuotesForRfq = async (rfqId: string) => {
    try {
      setQuotesLoading(true);
      const response = await rfqService.getQuotesForRfq(rfqId);
      if (response && response.items) {
        setQuotes(response.items);
      } else {
        setQuotes([]);
      }
    } catch (error: any) {
      console.error("Error fetching quotes:", error);
      showToast(
        "error",
        error?.response?.data?.message || "Failed to fetch quotes"
      );
      setQuotes([]);
    } finally {
      setQuotesLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params: any = {
        search: toolbar.q || undefined,
        status: toolbar.status !== "all" ? toolbar.status : undefined,
        buyerId: toolbar.buyerId !== "all" ? toolbar.buyerId : undefined,
        sellerId: toolbar.sellerId !== "all" ? toolbar.sellerId : undefined,
        categoryId:
          toolbar.categoryId !== "all" ? toolbar.categoryId : undefined,
        startDate: toolbar.from ? toolbar.from.toISOString() : undefined,
        endDate: toolbar.to ? toolbar.to.toISOString() : undefined,
      };

      const blob = await rfqService.exportRfqs(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rfqs-export-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast("success", "RFQs exported successfully");
    } catch (error: any) {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to export RFQs"
      );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "OPEN":
        return "bg-blue-500";
      case "CLOSED":
        return "bg-gray-500";
      case "CANCELLED":
        return "bg-red-500";
      case "DRAFT":
        return "bg-yellow-500";
      default:
        return "bg-gray-500";
    }
  };

  const formatDate = (dateStr: string | Date | null | undefined) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
  };

  const tableData = rfqs.map((rfq) => ({
    id: rfq.id,
    title: rfq.title,
    buyer: rfq.buyer?.company?.name || rfq.buyer?.email || "N/A",
    categories: rfq.categories?.map((c) => c.name).join(", ") || "N/A",
    status: <Badge className={getStatusColor(rfq.status)}>{rfq.status}</Badge>,
    quotesCount: (
      <div className="flex items-center gap-2">
        <span>{(rfq as any).quotes?.length || 0}</span>
        {(rfq as any).quotes?.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedRfqForQuotes(rfq.id);
              setQuotesDialogOpen(true);
              fetchQuotesForRfq(rfq.id);
            }}
            className="h-6 px-2 text-xs"
          >
            View
          </Button>
        )}
      </div>
    ),
    createdAt: formatDate(rfq.createdAt),
    expiryDate: formatDate(rfq.expiryDate),
    actions: (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to={`/rfq/${rfq.id}`}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </Link>
          </DropdownMenuItem>
          {rfq.status === "OPEN" && (
            <DropdownMenuItem
              onClick={() => setForceCloseDialog({ open: true, rfqId: rfq.id })}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Force Close
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  }));

  return (
    <div className="min-h-screen">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 p-4 rounded-lg shadow-lg max-w-[400px] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="flex-1 font-medium text-sm">{toast.message}</p>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
            aria-label="Close notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <main className="flex-1 p-6 max-w-[1400px] mx-auto w-full">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-semibold">RFQ Management</h2>
            <p className="text-sm text-gray-500">
              Manage all RFQs, force close, and suspend sellers from quoting.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExport} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            {loading && (
              <div className="flex items-center text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                Loading...
              </div>
            )}
          </div>
        </div>

        <DataToolBar
          state={toolbar}
          setState={setToolbarPartial}
          countries={[]}
          onExport={handleExport}
          onReset={() => {
            setToolbar({
              q: "",
              status: "all",
              country: "all",
              buyerId: "all",
              sellerId: "all",
              categoryId: "all",
              pageSize: 20,
            });
            setCurrentPage(1);
          }}
          onCreate={() => {}}
        />

        <hr className="my-4 border-t border-gray-300" />

        <Tabs defaultValue="rfqs" className="w-full">
          <TabsList>
            <TabsTrigger value="rfqs">RFQs ({total})</TabsTrigger>
            <TabsTrigger value="disputes">Disputes</TabsTrigger>
          </TabsList>

          <TabsContent value="rfqs">
            <Card>
              <CardHeader>
                <CardTitle>All RFQs ({total})</CardTitle>
              </CardHeader>
              <CardContent>
                {rfqs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No RFQs found
                  </div>
                ) : (
                  <>
                    <DataTable
                      data={tableData}
                      config={{
                        columns: [
                          { key: "title", label: "Title" },
                          { key: "buyer", label: "Buyer" },
                          { key: "categories", label: "Categories" },
                          { key: "status", label: "Status" },
                          { key: "quotesCount", label: "Quotes" },
                          { key: "createdAt", label: "Created" },
                          { key: "expiryDate", label: "Expiry" },
                          { key: "actions", label: "Actions" },
                        ],
                        styles: {
                          headerBg: "bg-gray-100",
                          headerText: "text-gray-700",
                          rowHover: "hover:bg-gray-50",
                          statusStyles: {
                            OPEN: {
                              background: "bg-green-100",
                              textColor: "text-green-800",
                            },
                            CLOSED: {
                              background: "bg-red-100",
                              textColor: "text-red-800",
                            },
                            CANCELLED: {
                              background: "bg-gray-100",
                              textColor: "text-gray-800",
                            },
                            DRAFT: {
                              background: "bg-yellow-100",
                              textColor: "text-yellow-800",
                            },
                          },
                        },
                      }}
                      rowsPerPage={toolbar.pageSize}
                    />
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-gray-500">
                        Page {currentPage} of {totalPages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCurrentPage((p) => Math.max(1, p - 1))
                          }
                          disabled={currentPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCurrentPage((p) => Math.min(totalPages, p + 1))
                          }
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="disputes">
            <AdminDisputeManagement />
          </TabsContent>
        </Tabs>
      </main>

      {/* Force Close Dialog */}
      <AlertDialog
        open={forceCloseDialog.open}
        onOpenChange={(open) => setForceCloseDialog({ open, rfqId: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Force Close RFQ</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to force close this RFQ? This will close the
              RFQ and reject all pending quotes. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleForceClose}
              disabled={actionLoading === forceCloseDialog.rfqId}
            >
              {actionLoading === forceCloseDialog.rfqId
                ? "Closing..."
                : "Force Close"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quotes Dialog */}
      <AlertDialog
        open={quotesDialogOpen}
        onOpenChange={(open) => {
          setQuotesDialogOpen(open);
          if (!open) {
            setSelectedRfqForQuotes(null);
            setQuotes([]);
          }
        }}
      >
        <AlertDialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Quotes for RFQ</AlertDialogTitle>
            <AlertDialogDescription>
              View all quotes submitted for this RFQ. You can suspend sellers
              from quoting.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            {quotesLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-2 text-gray-500">Loading quotes...</p>
              </div>
            ) : quotes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No quotes found
              </div>
            ) : (
              <div className="space-y-4">
                {quotes.map((quote) => (
                  <div key={quote.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium">
                            {quote.seller?.company?.name ||
                              quote.seller?.firstName ||
                              "N/A"}
                          </span>
                          <Badge className={getStatusColor(quote.status)}>
                            {quote.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Price: </span>
                            {quote.currency} {quote.price.toLocaleString()}
                          </div>
                          <div>
                            <span className="font-medium">Lead Time: </span>
                            {quote.leadTimeDays} days
                          </div>
                          <div>
                            <span className="font-medium">Seller Email: </span>
                            {quote.seller?.email || "N/A"}
                          </div>
                          <div>
                            <span className="font-medium">Submitted: </span>
                            {new Date(quote.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        {quote.message && (
                          <p className="mt-2 text-sm text-gray-600">
                            {quote.message}
                          </p>
                        )}
                      </div>
                      <div className="ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const seller = quote.seller;
                            if (seller) {
                              const isSuspended =
                                (seller as any).sellerStatus === "SUSPENDED";
                              setSuspendDialog({
                                open: true,
                                sellerId: seller.id,
                                suspend: !isSuspended,
                              });
                            }
                          }}
                        >
                          {(quote.seller as any)?.sellerStatus === "SUSPENDED"
                            ? "Unsuspend"
                            : "Suspend"}{" "}
                          Seller
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Suspend Seller Dialog */}
      <AlertDialog
        open={suspendDialog.open}
        onOpenChange={(open) =>
          setSuspendDialog({ open, sellerId: null, suspend: true })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {suspendDialog.suspend ? "Suspend" : "Unsuspend"} Seller from
              Quoting
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to{" "}
              {suspendDialog.suspend ? "suspend" : "unsuspend"} this seller from
              submitting quotes for RFQs?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSuspendSeller}
              disabled={actionLoading === suspendDialog.sellerId}
            >
              {actionLoading === suspendDialog.sellerId
                ? suspendDialog.suspend
                  ? "Suspending..."
                  : "Unsuspending..."
                : suspendDialog.suspend
                  ? "Suspend"
                  : "Unsuspend"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
